/**
 * ShopImage Component
 * Displays shop image with fallback priority:
 * 1. Profile image (shop_photos - uploaded by shop owner)
 * 2. Latest post image (from "deel je buit" posts)
 * 3. Random placeholder (1-14.png) - consistent per shop
 */

import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";

interface ShopImageProps {
  shopId: string;
  profileImageUrl?: string | null;
  latestPostImageUrl?: string | null;
  fallbackImageUrl?: string | null;
  className?: string;
  size?: "sm" | "md" | "lg" | "xl";
}

const PLACEHOLDER_COUNT = 14;

/**
 * Get random placeholder based on shop ID (consistent per shop)
 */
function getPlaceholderForShop(shopId: string): string {
  // Use shop ID to deterministically select a placeholder
  // This ensures the same shop always gets the same placeholder
  const hash = shopId.split("").reduce((acc, char) => {
    return acc + char.charCodeAt(0);
  }, 0);
  const placeholderNumber = (hash % PLACEHOLDER_COUNT) + 1;
  return `/placeholders/${placeholderNumber}.png`;
}

export function ShopImage({
  shopId,
  profileImageUrl,
  latestPostImageUrl,
  fallbackImageUrl,
  className,
  size = "md",
}: ShopImageProps) {
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set());

  // Determine which image to show based on priority
  const imageUrl = useMemo(() => {
    // Priority 1: Profile image (uploaded by shop owner)
    if (profileImageUrl && !failedImages.has(profileImageUrl)) {
      return profileImageUrl;
    }

    // Priority 2: Fallback main shop image
    if (fallbackImageUrl && !failedImages.has(fallbackImageUrl)) {
      return fallbackImageUrl;
    }

    // Priority 3: Latest post image (from "deel je buit")
    if (latestPostImageUrl && !failedImages.has(latestPostImageUrl)) {
      return latestPostImageUrl;
    }

    // Priority 4: Placeholder (random but consistent per shop)
    return getPlaceholderForShop(shopId);
  }, [profileImageUrl, fallbackImageUrl, latestPostImageUrl, shopId, failedImages]);

  const sizeClasses = {
    sm: "w-16 h-16",
    md: "w-24 h-24",
    lg: "w-32", // Width only, height controlled by className
    xl: "w-48",
  };

  const handleImageError = () => {
    // Mark current image as failed and try next fallback
    if (imageUrl && imageUrl !== getPlaceholderForShop(shopId)) {
      setFailedImages(prev => {
        const next = new Set(Array.from(prev));
        next.add(imageUrl);
        return next;
      });
    }
  };

  // Check if className includes h-full to allow full height
  const isFullHeight = className?.includes("h-full");

  return (
    <div
      className={cn(
        "relative flex-shrink-0 overflow-hidden bg-muted",
        !isFullHeight && sizeClasses[size],
        isFullHeight && "h-full w-full",
        className
      )}
      style={isFullHeight ? { height: "100%", width: "100%" } : undefined}
    >
      <img
        src={imageUrl}
        alt="Shop"
        className="w-full h-full object-cover"
        style={{ objectFit: "cover" }}
        onError={handleImageError}
        loading="lazy"
      />
    </div>
  );
}
